/* PO receiving page — public, mobile/iPad-first.
   The PO id in the URL is the auth. Warehouse staff land here via a
   shared link or QR code on a printed working document; for each
   line item they pick "received" / "partial" / "missing", optionally
   override the quantity, optionally take a photo, and optionally type
   their name. The status of the PO rolls up automatically: any line
   touched flips it to "partial"; all lines marked received flips it
   to "received". */
import { useEffect, useState } from "react";

export default function ReceiveView({ poId }) {
  const [po, setPo] = useState(null);
  const [error, setError] = useState("");
  const [byName, setByName] = useState(() => {
    try { return localStorage.getItem("pr:receiverName") || ""; } catch { return ""; }
  });
  const [pending, setPending] = useState(null); // line.id being submitted

  const refresh = async () => {
    try {
      const r = await fetch(`/api/receive/${encodeURIComponent(poId)}`);
      if (!r.ok) { setError(`Couldn't load this PO (${r.status})`); return; }
      const d = await r.json();
      setPo(d.po);
      setError("");
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { refresh(); }, [poId]); // eslint-disable-line

  useEffect(() => {
    try { localStorage.setItem("pr:receiverName", byName); } catch { /* ignore */ }
  }, [byName]);

  if (error) return <Shell><div style={errorBox}>{error}</div></Shell>;
  if (!po) return <Shell><div style={muted}>Loading…</div></Shell>;

  const submit = async (line, status, qty, photoDataUrl, notes) => {
    setPending(line.id);
    try {
      const r = await fetch(`/api/receive/${encodeURIComponent(po.id)}/line/${encodeURIComponent(line.id)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, qty, photoDataUrl, notes, byName: byName.trim() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
      await refresh();
    } catch (e) {
      alert(e.message || "Save failed");
    } finally {
      setPending(null);
    }
  };

  const STATUS_TINT = {
    draft:     { bg: "#F4F4F5", fg: "#52525B", label: "Draft" },
    sent:      { bg: "#EFF6FF", fg: "#1D4ED8", label: "Sent" },
    partial:   { bg: "#FEF3C7", fg: "#92400E", label: "Partially received" },
    received:  { bg: "#ECFDF5", fg: "#047857", label: "Received" },
    invoiced:  { bg: "#F5F3FF", fg: "#6D28D9", label: "Invoiced" },
    cancelled: { bg: "#F4F4F5", fg: "#A1A1AA", label: "Cancelled" },
  };
  const tint = STATUS_TINT[po.status] || STATUS_TINT.draft;
  const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <Shell>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Receiving</div>
          <h1 style={{ margin: "2px 0 4px", fontSize: 22, fontWeight: 800, color: "#000", letterSpacing: "-0.015em" }}>{po.number}</h1>
          <div style={{ fontSize: 14, color: "#3F3F46" }}>{po.vendorSnapshot?.name || "—"}</div>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>Issued {fmtDate(po.issueDate)} · Expected {fmtDate(po.expectedDate)}</div>
        </div>
        <span style={{ display: "inline-block", padding: "5px 11px", borderRadius: 99, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: tint.bg, color: tint.fg, flexShrink: 0 }}>{tint.label}</span>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={labelCss}>Your name (for the log)</label>
        <input
          value={byName}
          onChange={(e) => setByName(e.target.value)}
          placeholder="Type your name…"
          style={inputCss}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(po.lineItems || []).map(line => (
          <LineCard key={line.id} line={line} onSubmit={(s, q, ph, n) => submit(line, s, q, ph, n)} pending={pending === line.id} />
        ))}
      </div>

      {(po.receivingLog || []).length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>History</div>
          <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: 14, fontSize: 13, color: "#3F3F46", display: "flex", flexDirection: "column", gap: 6 }}>
            {(po.receivingLog || []).slice().reverse().map((e, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <span>{e.action}{e.byName ? ` · ${e.byName}` : ""}</span>
                <span style={{ color: "#9CA3AF", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{new Date(e.ts).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}

function LineCard({ line, onSubmit, pending }) {
  const r = line.received;
  const [open, setOpen] = useState(!r); // expand when not yet received
  const [qty, setQty] = useState(r?.qty != null ? String(r.qty) : String(line.qty));
  const [photoDataUrl, setPhotoDataUrl] = useState(r?.photo || null);
  const [notes, setNotes] = useState(r?.notes || "");
  const [photoErr, setPhotoErr] = useState("");

  const handleFile = (file) => {
    setPhotoErr("");
    if (!file) return;
    if (!file.type.startsWith("image/")) { setPhotoErr("Pick an image."); return; }
    if (file.size > 2 * 1024 * 1024) { setPhotoErr("Photo too large (max 2 MB)."); return; }
    const reader = new FileReader();
    reader.onload = (e) => setPhotoDataUrl(e.target?.result || null);
    reader.readAsDataURL(file);
  };

  const STATUS_BTN = (active, color) => ({
    flex: 1,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${active ? color : "#E5E7EB"}`,
    background: active ? color : "#fff",
    color: active ? "#fff" : "#3F3F46",
    fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif",
    cursor: "pointer",
    minHeight: 44,
  });

  const status = r?.status || null;
  const cardBg = status === "received" ? "#F0FDF4" : status === "partial" ? "#FFFBEB" : status === "missing" ? "#FEF2F2" : "#fff";
  const border = status === "received" ? "#A7F3D0" : status === "partial" ? "#FDE68A" : status === "missing" ? "#FECACA" : "#E5E7EB";

  return (
    <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, overflow: "hidden" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", width: "100%", border: "none", background: "transparent", cursor: "pointer", fontFamily: "Inter, sans-serif", textAlign: "left" }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#000" }}>{line.description || "(unnamed line)"}</div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
            {line.qty} {line.unit || ""}{r ? ` · received ${r.qty} ${line.unit || ""}` : ""}
          </div>
        </div>
        {r && <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 99, background: "#fff", color: status === "received" ? "#047857" : status === "partial" ? "#92400E" : "#B91C1C", border: "1px solid currentColor", textTransform: "uppercase", letterSpacing: 0.4 }}>{status}</span>}
        <span style={{ fontSize: 18, color: "#9CA3AF" }}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={labelCss}>Quantity received ({line.unit || "qty"})</label>
            <input type="number" min="0" step="1" value={qty} onChange={(e) => setQty(e.target.value)} style={inputCss} />
          </div>
          <div>
            <label style={labelCss}>Photo (optional)</label>
            <input type="file" accept="image/*" capture="environment" onChange={(e) => handleFile(e.target.files?.[0])} style={{ fontSize: 14, fontFamily: "Inter, sans-serif" }} />
            {photoErr && <div style={{ fontSize: 12, color: "#B91C1C", marginTop: 4 }}>{photoErr}</div>}
            {photoDataUrl && (
              <div style={{ marginTop: 8, position: "relative", display: "inline-block" }}>
                <img src={photoDataUrl} alt="Receiving photo" style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, border: "1px solid #E5E7EB" }} />
                <button onClick={() => setPhotoDataUrl(null)} style={{ position: "absolute", top: 6, right: 6, width: 26, height: 26, borderRadius: 99, border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 14, cursor: "pointer", lineHeight: 1 }}>×</button>
              </div>
            )}
          </div>
          <div>
            <label style={labelCss}>Notes (optional)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Damage, short ship, anything to flag…" style={{ ...inputCss, minHeight: 60, resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button disabled={pending} onClick={() => onSubmit("received", Math.max(0, parseFloat(qty) || 0), photoDataUrl, notes)} style={STATUS_BTN(status === "received", "#047857")}>{pending ? "Saving…" : "Received in full"}</button>
            <button disabled={pending} onClick={() => onSubmit("partial",  Math.max(0, parseFloat(qty) || 0), photoDataUrl, notes)} style={STATUS_BTN(status === "partial",  "#CC785C")}>Partial</button>
            <button disabled={pending} onClick={() => onSubmit("missing",  0, photoDataUrl, notes)}                                style={STATUS_BTN(status === "missing",  "#B91C1C")}>Missing</button>
          </div>
        </div>
      )}
    </div>
  );
}

const labelCss = { display: "block", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };
const inputCss = {
  padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB",
  fontSize: 14, fontFamily: "Inter, sans-serif", background: "#fff",
  outline: "none", width: "100%", boxSizing: "border-box", minHeight: 44,
};
const muted = { padding: 32, textAlign: "center", color: "#9CA3AF", fontSize: 14 };
const errorBox = { padding: 16, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, color: "#991B1B", fontSize: 14 };

function Shell({ children }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: "#FAFAFA",
      fontFamily: "Inter, sans-serif",
      WebkitTapHighlightColor: "transparent",
    }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "20px 16px 64px" }}>{children}</div>
    </div>
  );
}
